class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    // FF SEARCH BUILD (2026-08-10) [ff-search-two-panel]: snapshot whatever is
    // server-rendered inside [data-predictive-search] at load time — this is the
    // EMPTY-STATE markup (snippets/ff-search-panel.liquid, mode: 'empty') plus the
    // loading spinner. Restoring this snapshot (instead of re-fetching) is what lets
    // clearing the input / repeated open-close cycles return to a clean empty state
    // without ever going stale or "stuck" on old results.
    this.emptyStateMarkup = this.predictiveSearchResults ? this.predictiveSearchResults.innerHTML : '';

    this.backButton = this.querySelector('[data-ff-search-back]');

    // FF (2026-08-10) [ff-search-reveal-hardening]: BUG FIX — owner report: opening
    // desktop search sometimes popped a dropdown with only a bare input and no
    // designed content (recents/categories/best sellers), and mobile search
    // "breaks really easily". Root cause: revealing the results region
    // ([data-predictive-search], via Dawn's `predictive-search[open] .predictive-search
    // { display:block }` rule) was 100% dependent on this.input's native 'focus'
    // DOM event firing and being handled BEFORE anything reads layout/paints —
    // that event is dispatched by Dawn's <details-modal> component calling
    // trapFocus()->input.focus() from inside its own click handler, one call removed
    // from the actual click. That's an incidental side-channel, not a guaranteed
    // signal: under real-world conditions (slow paint, a background/inactive tab
    // regaining focus, a second rapid click before the first 'focus' dispatch
    // resolves) the focus event can be delayed or dropped entirely, leaving the
    // <details> visibly open with its [data-predictive-search] content still
    // display:none — exactly the "small empty panel" the owner saw, intermittently,
    // at varying viewports.
    // Fix: also drive the reveal off the <details> element's own native `toggle`
    // event, which fires reliably and synchronously whenever the panel's actual
    // open/closed state changes, independent of focus timing. This runs IN ADDITION
    // to the existing focus-based path (kept for a11y / keyboard flows), so opening
    // is now correct whichever signal lands first, and closing (Esc / outside click
    // on <details-modal>, which the toggle event also reports) keeps this component's
    // own open/loading state and body scroll-lock in sync instead of getting stuck.
    this.detailsEl = this.closest('details');
    if (this.detailsEl) {
      this.detailsEl.addEventListener('toggle', this.onDetailsToggle.bind(this));
    }

    this.setupEventListeners();
  }

  onDetailsToggle() {
    if (this.detailsEl.open) {
      if (!this.getQuery().length) {
        this.showEmptyState();
      } else if (this.getAttribute('open') !== 'true') {
        // A query is already typed (e.g. reopened after a prior search) but the
        // results region never got marked open — bring it back without a refetch.
        this.open();
      }
    } else if (this.isOpen) {
      // <details> was closed by something other than this component (Esc key,
      // outside click via <details-modal>'s own handler) — keep state in sync so
      // it doesn't read "open" internally on the next interaction.
      this.close();
    }
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // FF SEARCH BUILD: delegated click handling for the pieces that aren't <a> links
    // (query suggestions, recent-search chips, popular-search chips) plus recording
    // "last 5 submitted/clicked queries" to localStorage per the owner spec.
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.addEventListener('click', this.onResultsClick.bind(this));
    }
    if (this.backButton) {
      this.backButton.addEventListener('click', this.onBackClick.bind(this));
    }

    // FF SEARCH FIX (2026-08-10) [ff-mobile-search-hard-scroll-lock] cont'd: belt-
    // and-braces for the mobile takeover — the position:fixed body-freeze (see
    // lockBodyScroll()) already removes the underlying page as a scrollable target,
    // but block any touchmove that starts OUTSIDE the panel's own scrollable results
    // region too (the header row that holds the back arrow + input, and any bare
    // panel background), so a drag can't rubber-band/bounce the fixed body itself on
    // iOS Safari. Touches that start inside [data-predictive-search] (the actual
    // scrolling results list) are left alone so that region keeps scrolling normally.
    if (this.classList.contains('ff-mobile-search-bar__form')) {
      this.addEventListener('touchmove', (event) => {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        if (this.predictiveSearchResults && this.predictiveSearchResults.contains(event.target)) return;
        event.preventDefault();
      }, { passive: false });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    /* FF WAVE 3 (2026-08-12, search hammer): fresh user input invalidates any
       rescue/rewrite state from the previous query (the "Showing results for X"
       note + the one-ladder-pass-per-query guard). */
    this._ffOrigTerm = null;
    this._ffLadderFor = null;

    if (!searchTerm.length) {
      this.showEmptyState();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    this.input.value = this.input.value.replace(/^aos[-\s]+(?=\S*\d)/i, '');
    PredictiveSearch.saveRecentSearch(this.getQuery());
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showEmptyState();
      return;
    }

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    /* FF wip463 (owner, 2026-08-19): "when I click done on it, I don't want it to
       exit me out of it. I want to be able to see the whole mobile search."
       On iOS, dismissing the keyboard with Done fires focusout and leaves
       document.activeElement as <body> -- indistinguishable here from a real
       outside click, so the panel closed under him mid-search. Keyboard dismissal
       must NOT close the panel; a genuine click elsewhere still does, because the
       theme closes on outside-click/Escape separately. So: only close when focus
       actually moved to another real element outside this component. */
    setTimeout(() => {
      var ae = document.activeElement;
      var dismissedKeyboard = !ae || ae === document.body || ae === document.documentElement;
      if (dismissedKeyboard) return;
      if (!this.contains(ae)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      // FF SEARCH BUILD: clearing the input while focused returns to the empty
      // state (Recent/Popular + best sellers) instead of closing the panel outright.
      this.showEmptyState();
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  // FF SEARCH BUILD: back-arrow control in the mobile full-screen takeover — closes
  // the panel, clears focus, and returns the user to the page underneath.
  onBackClick(event) {
    event.preventDefault();
    this.close(true);
    this.input.blur();
  }

  // FF SEARCH BUILD: single delegated handler for everything inside the results
  // region that isn't a plain product/collection <a> link.
  onResultsClick(event) {
    /* FF FIX BATCH 2 2026-08-12 (item 2): clicking a suggestion ("living room set"),
       a popular-search chip, or a recent-search chip must RUN that search — owner
       report: they only prefilled the input (the in-panel re-render read as
       "nothing happened"). Now: save to Recents, mirror the term into the input
       (so the bar shows what's being searched during the load), and navigate
       straight to the results page. Category rows are plain <a> collection links
       and already navigate. type=product matches every other search entry point
       (wave-1 item 6). */
    const runTerm = (term) => {
      if (!term) return;
      PredictiveSearch.saveRecentSearch(term);
      this.input.value = term;
      window.location.assign('/search?q=' + encodeURIComponent(term) + '&type=product');
    };

    const suggestTerm = event.target.closest('[data-ff-suggest-term]');
    if (suggestTerm) {
      runTerm(suggestTerm.getAttribute('data-ff-suggest-term'));
      return;
    }

    const popularTerm = event.target.closest('[data-ff-popular-term]');
    if (popularTerm) {
      runTerm(popularTerm.getAttribute('data-ff-popular-term'));
      return;
    }

    const recentTerm = event.target.closest('[data-ff-recent-term]');
    if (recentTerm) {
      runTerm(recentTerm.getAttribute('data-ff-recent-term'));
      return;
    }

    const recentRemove = event.target.closest('[data-ff-recent-remove]');
    if (recentRemove) {
      event.preventDefault();
      event.stopPropagation();
      PredictiveSearch.removeRecentSearch(recentRemove.getAttribute('data-ff-recent-remove'));
      this.renderRecentSearches();
      return;
    }

    // Any other click on a real result (product/collection/"search for" link) —
    // count it as a "clicked query" for the Recent list, per spec.
    if (event.target.closest('a, button')) {
      PredictiveSearch.saveRecentSearch(this.getQuery());
    }
  }

  // ---------------------------------------------------------------------------
  // Recent searches (localStorage, max 5, most-recent-first). Static helpers so
  // every <predictive-search> instance (desktop bar, tablet drawer, mobile bar)
  // reads/writes the same shared list.
  // ---------------------------------------------------------------------------
  static RECENTS_KEY = 'ff_recent_searches';
  static RECENTS_MAX = 4;

  static getRecentSearches() {
    try {
      const raw = window.localStorage.getItem(PredictiveSearch.RECENTS_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  static saveRecentSearch(term) {
    term = (term || '').trim();
    if (!term) return;
    try {
      let list = PredictiveSearch.getRecentSearches().filter((t) => t.toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      list = list.slice(0, PredictiveSearch.RECENTS_MAX);
      window.localStorage.setItem(PredictiveSearch.RECENTS_KEY, JSON.stringify(list));
    } catch (e) {
      /* localStorage unavailable (private mode, etc.) — fail silently, Popular searches still show */
    }
  }

  static removeRecentSearch(term) {
    try {
      const list = PredictiveSearch.getRecentSearches().filter((t) => t !== term);
      window.localStorage.setItem(PredictiveSearch.RECENTS_KEY, JSON.stringify(list));
    } catch (e) {
      /* noop */
    }
  }

  static escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // Populates [data-ff-recent-list] and toggles the Recent/Popular wrappers inside
  // whichever markup currently occupies the results region (empty state OR the
  // zero-results panel, both rendered by snippets/ff-search-panel.liquid).
  renderRecentSearches() {
    const recentWrap = this.querySelector('[data-ff-recent-wrap]');
    const popularWrap = this.querySelector('[data-ff-popular-wrap]');
    const recentList = this.querySelector('[data-ff-recent-list]');
    if (!recentWrap || !recentList) return;

    const recents = PredictiveSearch.getRecentSearches();

    if (!recents.length) {
      recentWrap.hidden = true;
      if (popularWrap) popularWrap.hidden = false;
      return;
    }

    if (popularWrap) popularWrap.hidden = true;
    recentWrap.hidden = false;
    recentList.innerHTML = recents.map((term) => {
      const safe = PredictiveSearch.escapeHtml(term);
      return `<li class="ff-recent-item">` +
        `<button type="button" class="ff-recent-item__term" data-ff-recent-term="${safe}">${safe}</button>` +
        `<button type="button" class="ff-recent-item__remove" aria-label="Remove ${safe}" data-ff-recent-remove="${safe}">&times;</button>` +
        `</li>`;
    }).join('');
  }

  // FF SEARCH BUILD: restores the pristine empty-state markup (Recent/Popular +
  // Our best sellers + Popular categories) and opens the panel WITHOUT a network
  // request. Used on focus-with-empty-query and on clearing the input to nothing.
  showEmptyState() {
    if (!this.predictiveSearchResults) return;
    // FF (2026-08-10) [ff-search-stale-response-guard]: invalidate any in-flight
    // getSearchResults() fetch — see that method for why this matters. Clearing
    // the input (or reopening to empty) is a real signal that whatever was typed
    // before is no longer what the user wants shown.
    this._ffRequestId = (this._ffRequestId || 0) + 1;
    this.predictiveSearchResults.innerHTML = this.emptyStateMarkup;
    this.removeAttribute('results');
    this.removeAttribute('loading');
    this.renderRecentSearches();
    this.open();
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm, ffIsRescue) {
    // FF: Shopify's engine returns 0 for "aos-<partial sku>" while the bare partial works —
    // strip a leading AOS- on SKU-looking queries (must be followed by something with a digit).
    searchTerm = searchTerm.replace(/^aos[-\s]+(?=\S*\d)/i, '');

    /* FF WAVE 3 (2026-08-12, hammer items 2/6): high-confidence pre-fetch
       preprocessor — exact synonym/compound-spacing rewrites ("settee" ->
       "loveseat", "murphybed" -> "murphy bed") and NL phrase maps ("couch that
       turns into a bed" -> "sleeper sofa") from window.ffSearch. Only rewrites
       terms VERIFIED to return nothing natively (dictionary curated from the
       2026-08-12 search hammer, every target verified non-empty on this
       catalog). When a rewrite fires, the original is remembered so the render
       shows the honest "Showing results for X" line. ffIsRescue=true (ladder
       re-entry) skips this so a rescue candidate is fetched verbatim. */
    if (!ffIsRescue && window.ffSearch) {
      const ffPre = window.ffSearch.preRewrite(searchTerm);
      if (ffPre && ffPre !== searchTerm) {
        this._ffOrigTerm = searchTerm;
        searchTerm = ffPre;
      }
    }
    this._ffShownTerm = searchTerm;

    /* FF WAVE 3 (hammer 3.5 relevance guard, dropdown half): for a KNOWN
       not-carried brand/term the engine sometimes returns junk instead of
       nothing ("structube" -> a product matching "structure", "leons" ->
       Leonelle) — junk that looks like inventory. Force the designed
       zero-results panel instead: fetch with a never-matching tail so the
       server renders its noresults markup, then ffHandleZeroResults swaps the
       heading to the honest "We don't carry X" line (_ffNcForce short-circuits
       it past DYM + ladder). Brand+category queries (rest non-empty, e.g.
       "serta mattress") are left alone — the ladder strips the brand and shows
       the category. */
    this._ffNcForce = null;
    if (!ffIsRescue && window.ffSearch) {
      const ffNc = window.ffSearch.notCarried(searchTerm);
      if (ffNc && !ffNc.rest) {
        this._ffNcForce = ffNc;
        searchTerm = searchTerm + ' zzzffnoresult';
      }
    }

    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    // FF (2026-08-10) [ff-search-stale-response-guard]: BUG FIX — owner report:
    // mobile search "breaks really easily". Found under a 10-cycle open/type/
    // clear/close/reopen stress test: typing debounces at 300ms, then this
    // fetch() can easily still be in flight when the user clears the box or
    // closes the panel (a back-arrow tap, or focusout). The OLD code had no way
    // to cancel that in-flight request, so when it finally resolved it called
    // renderSearchResults() -> open() unconditionally — silently re-opening the
    // panel (and, on the mobile full-screen takeover, re-adding the
    // body-scroll-lock class) even though the user had already closed it and
    // moved on. That's the "stuck"/"reopens itself" behavior. Fix: stamp every
    // request with an incrementing id; showEmptyState()/close() bump the id to
    // invalidate whatever's in flight; a response only renders if it's still
    // the most recent request by the time it arrives.
    this._ffRequestId = (this._ffRequestId || 0) + 1;
    const requestId = this._ffRequestId;

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // FF SEARCH BUILD (2026-08-10): added resources[type]=query (query suggestions
    // for the left rail) and raised resources[limit] to 10 so the "Top results"
    // grid has enough products to fill 6 cards / categories has enough to filter
    // city-doorway dupes from.
    // FF FIX BATCH 2026-08-12 (item 2): the 2026-08-10 comment above claimed
    // limit_scope defaults to "each" — it does NOT; Shopify's default is "all",
    // which split the 10-limit ACROSS collection+product+query and starved the
    // product grid down to 3 cards (verified live: 3 without the param, 6 with).
    // resources[limit_scope]=each makes the limit per-type, so the Liquid
    // template's `limit: 6` products actually get 6 products to render
    // (desktop shows all 6; mobile CSS hides cards 5+ leaving 4, per spec).
    /* FF WAVE 3 (2026-08-12, hammer item 3.1): vendor ADDED to the searched
       fields — vendor data verified clean via Admin API 2026-08-12 (Signature
       Design by Ashley® / Aosom / Benchcraft® / GOWFB / IFDC / ...), so
       "aosom", "gowfb", "benchcraft recliner", "ashley sectional" now return
       that brand's products (verified live on suggest.json before shipping). */
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=collection,product,query&${encodeURIComponent('resources[limit]')}=10&${encodeURIComponent('resources[limit_scope]')}=each&section_id=predictive-search&resources[options][fields]=tag,title,product_type,variants.title,variants.sku,vendor`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          /* FF FIX BATCH 2026-08-12 (item 4): a failed fetch (429 rate-limit,
             transient network error) used to close() the whole panel mid-typing —
             one of the "search randomly closes while I'm typing" reports. The
             panel must only close on outside click / X / Escape: on error, just
             clear the loading state and keep showing whatever was already open. */
          if (requestId === this._ffRequestId) this.removeAttribute('loading');
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        // Cache it regardless (still useful for a later matching query), but only
        // render/open if nothing superseded this request while it was in flight.
        if (requestId !== this._ffRequestId) return;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        /* FF FIX BATCH 2026-08-12 (item 4): same as the !response.ok branch above —
           never close the panel on a fetch error; only clear the loading state. */
        if (requestId === this._ffRequestId) this.removeAttribute('loading');
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    /* FF WAVE 3 (2026-08-12, search hammer): zero-result renders now run the
       full rescue stack (not-carried message -> did-you-mean -> retry ladder,
       see ffHandleZeroResults below); non-zero renders that came from a
       rewrite/rescue get the honest "Showing results for X" line (wave-2
       pattern). Everything inside try/catch — the rescue layer must never
       break plain rendering. */
    try {
      const noresHeading = this.predictiveSearchResults.querySelector('.ff-search-noresults__heading');
      if (noresHeading) {
        this.ffHandleZeroResults(noresHeading);
      } else if (this._ffOrigTerm && this._ffShownTerm && this._ffOrigTerm !== this._ffShownTerm
                 && !this.predictiveSearchResults.querySelector('[data-ff-rescue-note]')) {
        const note = document.createElement('p');
        note.className = 'ff-search-rescue-note';
        note.setAttribute('data-ff-rescue-note', '');
        const strong = document.createElement('strong');
        strong.textContent = this._ffShownTerm;
        note.append('Showing results for ');
        note.appendChild(strong);
        note.append(' · ');
        const back = document.createElement('a');
        back.href = '/search?q=' + encodeURIComponent(this._ffOrigTerm) + '&type=product&ff_noexpand=1';
        back.textContent = 'Search instead for “' + this._ffOrigTerm + '”';
        note.appendChild(back);
        this.predictiveSearchResults.prepend(note);
      }
    } catch (e) { /* never let the rescue layer break rendering */ }

    this.setLiveRegionResults();
    this.open();
  }

  /* FF WAVE 3 (2026-08-12, search hammer items 3/4/5): dropdown zero-result
     rescue stack, in order:
     (a) known NOT-CARRIED brand/term ("ikea", "structube", "clearance"...) ->
         swap the heading to the honest "We don't carry X" line and stop — the
         panel's best sellers + popular categories are the approved answer.
     (b) did-you-mean (FIX BATCH item 8 engine, lexicon now includes vendor
         names) — injected immediately; the button reuses [data-ff-suggest-term]
         so the delegated onResultsClick handler runs it.
     (c) retry-on-empty ladder: qualifier/price-tail strip ("recliner under
         1000" -> "recliner"), stopword+"for ..."-tail strip ("sauna for 4
         people" -> "sauna"), compound split/join, then token drops front-first.
         Candidates are probed cheaply against suggest.json (1 product) and the
         first non-empty one re-renders the panel via getSearchResults(cand,
         true), which then shows the "Showing results for X" note above.
         One ladder pass per typed query (_ffLadderFor guard); a stale pass is
         abandoned whenever _ffRequestId moves on (same token every other
         in-flight fetch in this component respects). */
  ffHandleZeroResults(noresHeading) {
    /* forced not-carried render (relevance guard above) — heading swap only */
    if (this._ffNcForce) {
      noresHeading.textContent = this._ffNcForce.message;
      return;
    }

    const orig = this._ffOrigTerm || this._ffShownTerm || this.getQuery();
    if (!orig) return;

    const nc = window.ffSearch && window.ffSearch.notCarried(orig);
    if (nc && !nc.rest) {
      noresHeading.textContent = nc.message;
      return;
    }

    let ffDym = null;
    if (window.ffDidYouMean && !this.predictiveSearchResults.querySelector('[data-ff-didyoumean]')) {
      ffDym = window.ffDidYouMean(orig);
      if (ffDym) {
        const p = document.createElement('p');
        p.className = 'ff-search-didyoumean';
        p.setAttribute('data-ff-didyoumean', '');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ff-search-didyoumean__term';
        btn.setAttribute('data-ff-suggest-term', ffDym);
        btn.textContent = ffDym;
        p.append('Did you mean ');
        p.appendChild(btn);
        p.append('?');
        noresHeading.insertAdjacentElement('afterend', p);
      }
    }

    if (!window.ffSearch || this._ffLadderFor === orig) return;
    this._ffLadderFor = orig;
    const seen = {};
    /* hammer 3.3/3.4: the did-you-mean correction is the ladder's LAST rung and
       AUTO-RUNS ("matress" -> mattress results + the honest note) instead of
       waiting for a click — the injected line above still renders in the
       meantime and stays if every probe misses. */
    const cands = (nc && nc.rest ? [nc.rest] : [])
      .concat(window.ffSearch.candidates(orig))
      .concat(ffDym ? [ffDym] : [])
      .filter((c) => c && c !== orig && !seen[c] && (seen[c] = 1))
      .slice(0, 7);
    if (!cands.length) return;
    const reqId = this._ffRequestId;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const tryNext = (idx) => {
      if (idx >= cands.length || reqId !== this._ffRequestId) return;
      const cand = cands[idx];
      fetch(root + 'search/suggest.json?q=' + encodeURIComponent(cand) +
            '&' + encodeURIComponent('resources[type]') + '=product' +
            '&' + encodeURIComponent('resources[limit]') + '=1' +
            '&' + encodeURIComponent('resources[options][fields]') + '=tag,title,product_type,variants.title,variants.sku,vendor',
            { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (reqId !== this._ffRequestId) return;
          const prods = data && data.resources && data.resources.results && data.resources.results.products;
          if (prods && prods.length) {
            this._ffOrigTerm = orig;
            this.getSearchResults(cand, true);
          } else {
            tryNext(idx + 1);
          }
        })
        .catch(() => { /* ladder is best-effort; the DYM/no-results panel stays */ });
    };
    tryNext(0);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    // FF SEARCH BUILD: the mobile persistent bar's <predictive-search> becomes a
    // full-screen takeover at <750px (see ff-search.css). Lock body scroll only
    // for that instance so the desktop dropdown / tablet drawer behavior (which
    // already manage their own scroll) are untouched.
    if (this.classList.contains('ff-mobile-search-bar__form') && window.matchMedia('(max-width: 749px)').matches) {
      // FF SEARCH FIX (2026-08-10) [ff-mobile-search-hard-scroll-lock]: owner report —
      // the page still scrolls BEHIND the open takeover. body.ff-search-lock's
      // `overflow:hidden` (below) is not reliable on mobile Safari/Chrome — touch drags
      // can still rubber-band/pan the underlying document even with overflow:hidden on
      // <body>, because that CSS property only blocks the *scrollbar*/programmatic
      // scroll path, not touch-driven viewport panning. The robust cross-browser fix is
      // the "frozen body" pattern: pull the page out of flow entirely
      // (position:fixed) at its current scroll offset (as a negative `top`), so there
      // is nothing left for a touch drag to scroll — then undo it and re-scroll to that
      // offset on close. PredictiveSearch.lockBodyScroll()/unlockBodyScroll() (static
      // helpers below) do this; kept as statics (like the recent-searches helpers)
      // since only one instance of the mobile takeover can be open at a time and this
      // must be exactly symmetric regardless of which instance opened/closes it.
      /* FF FIX BATCH 2026-08-12 (item 13 / mobile): open() runs on EVERY result
         render (each typed query), and each call incremented the lock depth —
         but close() only ever decrements once, so after typing 2+ queries the
         back arrow left the page permanently scroll-frozen (the long-reported
         "can't scroll after closing search, needs a refresh" mobile bug,
         reproduced live: depth 4 after four queries, one close, class stuck).
         Lock only when not already locked: depth then never exceeds 1 and one
         close always fully unlocks. */
      if (!document.body.classList.contains('ff-search-lock')) {
        PredictiveSearch.lockBodyScroll();
      }
    }
  }

  close(clearSearchTerm = false) {
    // FF (2026-08-10) [ff-search-stale-response-guard]: invalidate any in-flight
    // getSearchResults() fetch on every close (back arrow, outside click, Esc via
    // the <details> toggle handler) — not just on explicit clear — so a slow
    // response can never resurrect a panel the user already closed.
    this._ffRequestId = (this._ffRequestId || 0) + 1;

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;

    if (this.classList.contains('ff-mobile-search-bar__form')) {
      PredictiveSearch.unlockBodyScroll();
    }
  }

  // ---------------------------------------------------------------------------
  // FF SEARCH FIX (2026-08-10) [ff-mobile-search-hard-scroll-lock]: "frozen body"
  // scroll lock for the mobile full-screen takeover — static so open()/close() on
  // any instance stay symmetric (see open() above for the "why not just
  // overflow:hidden" rationale). Guarded via a static counter, not a boolean, so a
  // stray re-entrant open() (e.g. re-focusing an already-open takeover) can never
  // cause a premature unlock from a close() that fires while still conceptually
  // "open" elsewhere in the call stack.
  // ---------------------------------------------------------------------------
  static _ffScrollLockDepth = 0;
  static _ffSavedScrollY = 0;

  static lockBodyScroll() {
    if (PredictiveSearch._ffScrollLockDepth === 0) {
      PredictiveSearch._ffSavedScrollY = window.scrollY;
      const body = document.body;
      body.classList.add('ff-search-lock');
      body.style.position = 'fixed';
      body.style.top = `-${PredictiveSearch._ffSavedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
    }
    PredictiveSearch._ffScrollLockDepth += 1;
  }

  static unlockBodyScroll() {
    if (PredictiveSearch._ffScrollLockDepth === 0) return;
    PredictiveSearch._ffScrollLockDepth -= 1;
    if (PredictiveSearch._ffScrollLockDepth > 0) return;

    const body = document.body;
    const y = PredictiveSearch._ffSavedScrollY;
    body.classList.remove('ff-search-lock');
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    // FF (2026-08-10) [ff-mobile-search-scroll-restore]: put the page back exactly
    // where it was before the takeover opened — freeing `position:fixed` snaps the
    // page to scrollY:0 first, so the restore has to be an explicit scrollTo, not
    // just "leave it alone". setTimeout(0) rather than requestAnimationFrame — this
    // still runs after the current reflow/paint cycle (a macrotask, so it lands
    // after the inline-style removal above has taken effect), but unlike rAF it
    // isn't gated on the page actually getting a paint tick, which a
    // backgrounded/throttled mobile tab isn't guaranteed to deliver promptly.
    setTimeout(() => window.scrollTo(0, y), 0);
  }

  // FF SEARCH FIX (2026-08-11) [ff-mobile-search-lock-safety-net]: owner report —
  // after closing mobile search, the page sometimes can't be scrolled and needs a
  // refresh. Audited every exit path: back-arrow (onBackClick -> close(true)),
  // outside click / Esc on desktop-tablet (native <details> toggle ->
  // onDetailsToggle -> close()), and focus leaving the component (onFocusOut ->
  // close()) all already call close()/unlockBodyScroll() correctly. The gap is
  // BROWSER BACK: tapping the phone/browser back button while the mobile takeover
  // is open navigates away (or, on iOS/Android, the page can be frozen into the
  // back-forward cache mid-lock) WITHOUT ever running this component's own close()
  // — nothing in the normal open/close lifecycle observes browser navigation, so
  // body.ff-search-lock + the position:fixed inline styles can survive into
  // whatever the user lands on next (a bfcache-restored copy of THIS page, in
  // particular, since bfcache snapshots and restores the live DOM/style state
  // as-is, lock included). Belt-and-braces fix: force the lock off, unconditionally
  // and regardless of the depth counter, on 'pagehide' (about to be hidden/
  // unloaded/bfcached — unlock before that state gets captured) and on 'pageshow'
  // with event.persisted (a bfcache restore — unlock again in case a locked state
  // was captured before this fix, or by any other future exit path) and on
  // 'visibilitychange' going to 'hidden' (covers app-switch / tab-switch /
  // home-button cases where 'pagehide' timing can vary across mobile browsers).
  // Module-level listeners (registered once) rather than per-instance, since the
  // lock itself is already tracked as static/shared state.
  static forceUnlockBodyScroll() {
    const body = document.body;
    if (PredictiveSearch._ffScrollLockDepth === 0 && !body.classList.contains('ff-search-lock')) return;
    PredictiveSearch._ffScrollLockDepth = 0;
    const y = PredictiveSearch._ffSavedScrollY;
    body.classList.remove('ff-search-lock');
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    window.scrollTo(0, y);
  }
}

customElements.define('predictive-search', PredictiveSearch);

(function () {
  function safetyUnlock() {
    PredictiveSearch.forceUnlockBodyScroll();
  }
  window.addEventListener('pagehide', safetyUnlock);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) safetyUnlock();
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') safetyUnlock();
  });
})();

/* FF SEARCH FIX (2026-08-10) [ff-search-bar-forward]: owner report — opening the
   desktop search panel rendered a visibly SECOND search input below the header bar.
   header.liquid's .header__search--bar now has a real <input id="Search-Header-Bar">
   as the bar's own typing surface (was a decorative <span>); the original
   #Search-In-Modal-1 input that Dawn's PredictiveSearch component (above) actually
   drives is still in the DOM (the component needs a real <input type="search"> inside
   a <form> to submit/query against) but is now visually hidden via CSS
   (ff-header-ext.css [ff-search-single-input]). This forwards typing/focus/Enter/Esc
   from the visible bar input to the real hidden one — dispatching genuine DOM events
   (not calling component internals directly) so the exact same listeners the
   component already wires up in setupEventListeners() above keep driving everything:
   results fetch (debounced 'input'), open-on-focus, arrow-key navigation, Enter to
   select/submit, and Esc/outside-click to close (native <details> behavior, untouched).
   Scoped to the desktop bar only via the fixed ids below — the tablet icon-drawer bar
   and the mobile persistent bar keep their own existing (single-input, unaffected)
   markup. */
(function () {
  function init() {
    var barInput = document.getElementById('Search-Header-Bar');
    var realInput = document.getElementById('Search-In-Modal-1');
    if (!barInput || !realInput) return;

    var detailsEl = barInput.closest('details');
    var psEl = realInput.closest('predictive-search');

    function syncBarFromReal() {
      if (barInput.value !== realInput.value) barInput.value = realInput.value;
    }

    // Typing in the visible bar -> mirror into the real input and dispatch a real
    // 'input' event so the component's own debounced onChange() fires.
    barInput.addEventListener('input', function () {
      realInput.value = barInput.value;
      realInput.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Focusing the visible bar -> tell the component the (fake, from its
    // perspective) input was focused, so onFocus()'s open/empty-state logic runs.
    // Native <details>/<summary> click already opens the panel; this covers
    // keyboard/programmatic focus too.
    barInput.addEventListener('focus', function () {
      realInput.dispatchEvent(new Event('focus', { bubbles: true }));
    });

    barInput.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        // Enter submits — forward to the real form (the visible bar input lives in
        // <summary>, outside <form>, so it can't submit natively).
        event.preventDefault();
        realInput.value = barInput.value;
        var form = realInput.closest('form');
        if (form) {
          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        }
        return;
      }
      if (event.key === 'Escape') {
        if (detailsEl) detailsEl.open = false;
        barInput.blur();
        return;
      }
      // Arrow-key navigation / anything else the component listens for on keyup —
      // forward as a real event on the component element itself (setupEventListeners
      // binds keyup/keydown on `this`, i.e. the <predictive-search> element, via
      // bubbling from its own input; the bar input lives outside that element so its
      // keydown never bubbles there natively).
      if (psEl && (event.code === 'ArrowUp' || event.code === 'ArrowDown')) {
        event.preventDefault();
        psEl.dispatchEvent(new KeyboardEvent('keyup', { code: event.code, key: event.key, bubbles: true }));
      }
    });

    // Clicking a recent/popular/suggested term sets realInput.value + calls
    // realInput.focus() (a no-op now that it's hidden) — mirror the value back to the
    // visible bar so what the user sees stays in sync.
    if (psEl) {
      psEl.addEventListener('click', function () {
        setTimeout(syncBarFromReal, 0);
      });
    }

    // Reopening after a prior close, or the component clearing the term on close —
    // keep the visible bar in sync with the real input's state.
    if (detailsEl) {
      detailsEl.addEventListener('toggle', function () {
        setTimeout(syncBarFromReal, 0);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* FF FIX BATCH 2026-08-12 (item 4) [ff-search-open-guard]: the search overlay must
   close ONLY on (a) click outside the panel, (b) the X button, (c) Escape. Dawn's
   details-modal toggles CLOSED on ANY summary click — and the desktop bar's visible
   input (#Search-Header-Bar) lives INSIDE the <summary>, so clicking the input (or
   the magnifier icon) while the panel was open closed it. Fix: a capture-phase
   guard on every header-search summary that, while its <details> is already open,
   swallows the click before details-modal's own (bubble-phase) handler can see it.
   Opening (closed -> open) is untouched — the guard returns early when closed.
   Outside-click (details-modal's onBodyClick), the X (.modal__close-button), and
   Escape all live outside the summary, so all three still close normally. */
(function () {
  function bindOpenGuards() {
    document.querySelectorAll('.header__search summary').forEach(function (summary) {
      if (summary.dataset.ffOpenGuard) return;
      summary.dataset.ffOpenGuard = '1';
      summary.addEventListener('click', function (event) {
        var details = summary.closest('details');
        if (!details || !details.open) return; // closed -> let details-modal open it
        event.preventDefault();   // block the native <details> toggle
        event.stopPropagation();  // block details-modal's onSummaryClick close
        var barInput = summary.querySelector('input');
        if (barInput && document.activeElement !== barInput) {
          try { barInput.focus({ preventScroll: true }); } catch (e) { barInput.focus(); }
        }
      }, true);
    });
  }
  /* FF FIX BATCH 2026-08-12 (item 4b): the X close button was DEAD on the desktop
     bar — Dawn's details-modal binds its close handler to the FIRST
     button[type="button"] inside the details container, and the ff-search-panel
     empty-state markup (recent/popular chips, all type="button") now precedes the
     X in the DOM, so the binding landed on a popular-search chip instead
     (pre-existing since the 2026-08-10 search build). Delegated, explicit close
     for the actual X controls in any header search modal. */
  document.addEventListener('click', function (event) {
    var xBtn = event.target.closest('.header__search .modal__close-button, .header__search .search-modal__close-button');
    if (!xBtn) return;
    var dm = xBtn.closest('details-modal');
    if (dm && typeof dm.close === 'function') {
      dm.close();
    } else {
      var d = xBtn.closest('details');
      if (d) d.removeAttribute('open');
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindOpenGuards);
  } else {
    bindOpenGuards();
  }
})();

/* FF FIX BATCH 2026-08-12 (item 8) [ff-did-you-mean]: client-side typo tolerance.
   Fuzzy-matches a query against known catalog names (category vocabulary + product
   family first-words with 3+ published products, pulled from the Admin API on
   2026-08-12) using Damerau-Levenshtein distance: <=1 for queries up to 4 chars,
   <=2 for longer ones. Multi-word queries are also corrected word-by-word.
   Exposed as window.ffDidYouMean(query) -> "suggestion" | null. Consumed by
   renderSearchResults above (dropdown zero-results) and by the /search results
   page injector at the bottom of this file. */
window.ffDidYouMean = (function () {
  var NAMES = (
    'sectional,sectionals,sofa,sofas,loveseat,loveseats,recliner,recliners,couch,couches,' +
    'coffee table,accent table,console table,end table,side table,tv stand,tv stands,' +
    'ottoman,ottomans,bench,benches,accent chair,accent chairs,bed,beds,bed frame,' +
    'bunk bed,murphy bed,cabinet bed,headboard,headboards,dresser,dressers,nightstand,' +
    'nightstands,mattress,mattresses,dining set,dining table,dining chair,dining chairs,' +
    'bar stool,bar stools,counter stool,sideboard,buffet,desk,desks,office chair,' +
    'bookshelf,bookcase,patio,sauna,saunas,massage chair,sleeper,sleeper sofa,futon,' +
    'wardrobe,armoire,gazebo,greenhouse,shed,sheds,rug,daybed,chaise,vanity,mirror,' +
    'bedroom set,loft bed,console,cabinet,' +
    'olivia,bliss,haven,nirvana,serenity,aurora,nordic,cuddle plush,jordan,top tier,' +
    'long beach,hailey,liberty,zen fire,' +
    'alphons,altari,altyra,amiata,anarasia,ardenfield,arloster,ashbryn,aviemore,bandon,' +
    'baystorm,beachcroft,belachime,bellaby,binterglen,bixler,blairhurst,bolanburg,' +
    'bolsena,bostwick,breckington,bridgepoint,brizmore,broachmyn,bronyan,brozmont,' +
    'bryceview,burkhaus,cabalynn,cabinella,cadmori,caitbrook,camdill,carianna,cashton,' +
    'cayboni,chalanna,chirason,cielden,covetown,culverbach,danabrin,dantenton,danum,' +
    'danziar,darcy,deanlow,derekson,draycoll,dressonni,drystan,elbrim,erinslane,' +
    'feddinger,finch,foreside,fortman,fraluna,frandern,frohn,gaelon,gerridan,gesthaven,' +
    'graystorm,greddinton,greenbriar,haddigan,hallityn,harperfeld,havalance,havonplane,' +
    'hazela,heartcort,hollivern,hurley,hyanna,juararo,kallari,kanlow,kanwyn,karallin,' +
    'keerwick,kendanport,kimora,kinlanni,korestone,kozlani,laguna,landocken,lanolee,' +
    'laresview,lasinger,lavenhorne,lavinton,lawroy,lettner,lindyn,lodanna,loyaska,' +
    'luigi,lyncott,maeling,makidern,maribel,marxmore,maxton,maylee,maystonna,modmax,' +
    'monaghan,monterey,moriville,neymorton,oliandon,olten,onita,pamytta,parklynn,' +
    'paxberry,prendonea,radkin,rainier,ralene,ralinksi,rannis,ravenel,realyn,ridgester,' +
    'robbinsdale,rowanbeck,roxmere,rusticott,santorine,savonport,seton,shaehartin,' +
    'sharlance,shawburn,shaybrock,simmenfort,skempton,socalle,stelsie,stoneland,' +
    'stonemeade,sturlayne,taffenbrook,tallow,telora,trinell,triston,urbinforte,' +
    'valebeck,visola,wildenauer,willarae,willowbend,willowton,wistenpine,wynnlow,zuraleus,' +
    /* FF WAVE 3 (2026-08-12, hammer item 3.4): vendor names (verified via Admin
       API productVendors 2026-08-12) so "ashely" -> "ashley" etc., plus a few
       synonym-dictionary heads so typos of them correct first and the
       preprocessor then maps them to carried vocabulary. */
    'ashley,aosom,benchcraft,gowfb,ifdc,osaki,nectar,millennium,sierra sleep,' +
    'chesterfield,settee,davenport,loveseats,heidi,wallie,skye,lift chair,wall bed'
  ).split(',');

  // Damerau-Levenshtein (optimal string alignment) with early band exit.
  function dist(a, b, max) {
    var al = a.length, bl = b.length;
    if (Math.abs(al - bl) > max) return max + 1;
    var prevPrev = [], prev = [], cur = [], i, j;
    for (j = 0; j <= bl; j++) prev[j] = j;
    for (i = 1; i <= al; i++) {
      cur = [i];
      var rowMin = i;
      for (j = 1; j <= bl; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, prevPrev[j - 2] + 1);
        }
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      prevPrev = prev;
      prev = cur;
    }
    return prev[bl];
  }

  function bestFor(word) {
    word = word.toLowerCase();
    if (word.length < 3) return null;
    var max = word.length <= 4 ? 1 : 2;
    var best = null, bestD = max + 1;
    for (var i = 0; i < NAMES.length; i++) {
      var n = NAMES[i];
      if (n === word) return null; // already a known name — no correction needed
      var d = dist(word, n, max);
      if (d < bestD) { bestD = d; best = n; if (d === 1 && word.length > 4) break; }
    }
    return bestD <= max ? best : null;
  }

  return function (query) {
    query = (query || '').toLowerCase().trim().replace(/\s+/g, ' ');
    if (query.length < 3 || query.length > 40) return null;
    // whole-query match first (covers multi-word names like "coffee table")
    var whole = bestFor(query);
    if (whole) return whole;
    // then word-by-word for multi-word queries ("cofee tabel" -> "coffee table")
    var words = query.split(' ');
    if (words.length < 2 || words.length > 4) return null;
    var corrected = [], changed = false;
    for (var i = 0; i < words.length; i++) {
      var c = words[i].length >= 3 ? bestFor(words[i]) : null;
      if (c) { corrected.push(c); changed = true; } else { corrected.push(words[i]); }
    }
    return changed ? corrected.join(' ') : null;
  };
})();

/* FF WAVE 3 (2026-08-12, search hammer) [ff-search-rewrite-engine]: the shared
   query-preprocessor + retry-ladder brain. Everything here was curated from the
   2026-08-12 hammer report (455 graded queries): every EXACT/TOKENS source term
   was VERIFIED to return zero results natively, and every rewrite target was
   VERIFIED non-empty on this catalog via suggest.json before shipping.
   Consumers: PredictiveSearch (dropdown pre-fetch rewrite + zero-result ladder)
   and the /search results-page rescue IIFE below. */
window.ffSearch = (function () {
  // whole-word substring rewrites (longest-first), applied inside the query
  var EXACT = {
    'settee': 'loveseat',
    'love seat': 'loveseat',
    'luvseat': 'loveseat',
    'lovseat': 'loveseat',
    'tv unit': 'tv stand',
    'tvstand': 'tv stand',
    'hide a bed': 'sleeper sofa',
    'hideabed': 'sleeper sofa',
    'pull out bed': 'sleeper sofa',
    'pullout bed': 'sleeper sofa',
    'pull out couch': 'sleeper sofa',
    'pullout couch': 'sleeper sofa',
    'pull out sofa': 'sleeper sofa',
    'pullout sofa': 'sleeper sofa',
    'sofabed': 'sleeper sofa',
    'sleepersofa': 'sleeper sofa',
    'fold away bed': 'murphy bed',
    'foldaway bed': 'murphy bed',
    'fold up bed': 'murphy bed',
    'fold out bed': 'murphy bed',
    'foldout bed': 'murphy bed',
    'easy chair': 'recliner',
    'lift chair': 'power lift recliner',
    'lazy boy': 'recliner',
    'lazyboy': 'recliner',
    'la z boy': 'recliner',
    'footstool': 'ottoman',
    'foot stool': 'ottoman',
    'foot rest': 'ottoman',
    'hassock': 'ottoman',
    'bureau': 'dresser',
    'highboy': 'chest',
    'shelving unit': 'bookcase',
    'book case': 'bookcase',
    'book shelf': 'bookshelf',
    'wardrobe': 'storage cabinet',
    'armoire': 'storage cabinet',
    'night table': 'nightstand',
    'nitestand': 'nightstand',
    'head board': 'headboard',
    'davenport': 'sofa',
    'divan': 'sofa',
    'chesterfield': 'sofa',
    'murphybed': 'murphy bed',
    'bunkbed': 'bunk bed',
    'coffeetable': 'coffee table',
    'bedframe': 'bed frame',
    'kingbed': 'king bed',
    'queenbed': 'queen bed',
    'endtable': 'end table',
    'sidetable': 'side table',
    'diningtable': 'dining table',
    'diningset': 'dining set',
    'officechair': 'office chair',
    'officedesk': 'office desk',
    'platformbed': 'platform bed',
    'cabinetbed': 'cabinet bed',
    'floorlamp': 'floor lamp',
    'tablelamp': 'table lamp',
    'wallclock': 'wall clock',
    'wallmirror': 'wall mirror',
    'patioset': 'patio set',
    'patioumbrella': 'patio umbrella',
    'firetable': 'fire table',
    'chestofdrawers': 'chest of drawers',
    'lshaped': 'l shaped',
    'popup': 'pop-up'
  };
  var EXACT_KEYS = Object.keys(EXACT).sort(function (a, b) { return b.length - a.length; });

  // NL phrase maps (hammer item 6) — first match wins, replaces the whole query
  var PHRASES = [
    [/\bhide?s a bed\b/, 'cabinet bed'],
    [/\b(couch|sofa|sectional|futon)\b.*\b(turn|turns|convert|converts)s?\b.*\bbed\b/, 'sleeper sofa'],
    [/\bturns? into a bed\b/, 'sleeper sofa'],
    [/\bbed\b.*\bfolds? (into|out of|down from) (the )?wall\b/, 'murphy bed'],
    [/\bfolds? into the wall\b/, 'murphy bed'],
    [/\b(lift ?top|lifts? up)\b.*\btable\b|\btable\b.*\blifts? (up|top)\b/, 'lift top coffee table']
  ];
  // elderly/senior + seating context -> power lift recliner
  var ELDERLY = /\b(elderly|senior|seniors|old(er)? (person|people|parents?)|aging|mobility)\b/;
  var SEATING = /\b(chair|chairs|recliner|recliners|lift|seat)\b/;

  // not-carried brands/terms (hammer section 4 + 2.1 near-miss junk cases)
  var NOT_CARRIED = ['ikea', 'wayfair', 'endy', 'casper', 'sealy', 'tempurpedic', 'tempur pedic',
    'serta', 'structube', 'leons', 'leon s', 'the brick', 'costco', 'walmart', 'amazon',
    'home depot', 'homesense', 'west elm', 'pottery barn', 'crate and barrel', 'article',
    'wazo', 'mobilia', 'jysk', 'sleep country', 'clearance', 'liquidation'];
  // carried-brand tokens (vendor data verified 2026-08-12): strip-to-category ladder rung
  var CARRIED_BRANDS = ['ashley', 'ashleys', 'signature design', 'benchcraft', 'aosom', 'gowfb',
    'ifdc', 'homcom', 'osaki', 'nectar', 'millennium', 'sierra sleep'];

  var STOPWORDS = ' up the a an and or for with my me of in on to that this is it i need want looking please any some be are size ';
  var QUALIFIER_WORDS = /\b(cheap(est)?|affordable|inexpensive|budget)\b/g;
  var PRICE_TAIL = /\bunder \$?\d[\d,]*\b/g;
  var INCHES = /\b\d+ ?(inch(es)?|in|ft|foot|feet)\b/g;
  var SIZE_ADJ = /\b(small|little|compact|big|large|huge|mini|tiny)\b/g;

  // lexicon for the generic joined-word splitter (both halves must be words)
  var WORDS = {};
  ('murphy bed beds bunk sofa sofas sleeper coffee table tables tv stand stands end side king queen twin full ' +
   'dining set sets office chair chairs desk desks platform cabinet floor wall lamp lamps clock mirror patio ' +
   'umbrella fire book case shelf frame night head board love seat recliner recliners dresser dressers day ' +
   'nightstand mattress ottoman bench loveseat sectional stool bar console storage futon daybed vanity rug rugs ' +
   'shed sheds gazebo sauna pop shoe rack room living bedroom accent corner canopy garden lift top power glass wood')
    .split(' ').forEach(function (w) { WORDS[w] = 1; });

  function norm(q) {
    return (q || '').toLowerCase()
      .replace(/[“”‘’"']/g, ' ')
      .replace(/[-_/(),.&+]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function replaceWord(hay, needle, target) {
    // whole-word substring replace (needle already normalized, may be multi-word)
    var re = new RegExp('(^| )' + needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '( |$)', 'g');
    return hay.replace(re, '$1' + target + '$2').replace(/\s+/g, ' ').trim();
  }

  function applyDictionary(q) {
    for (var i = 0; i < EXACT_KEYS.length; i++) {
      var k = EXACT_KEYS[i];
      if ((' ' + q + ' ').indexOf(' ' + k + ' ') !== -1) q = replaceWord(q, k, EXACT[k]);
    }
    return q;
  }

  function applyPhrases(q) {
    for (var i = 0; i < PHRASES.length; i++) {
      if (PHRASES[i][0].test(q)) return PHRASES[i][1];
    }
    if (ELDERLY.test(q) && SEATING.test(q)) return 'power lift recliner';
    return q;
  }

  // high-confidence preprocessor: dictionary + phrase maps only (run pre-fetch)
  function preRewrite(raw) {
    var q = norm(raw);
    if (!q || q.length > 60) return null;
    var out = applyPhrases(q);
    if (out === q) out = applyDictionary(q);
    return out !== q ? out : null;
  }

  function stripQualifiers(q) {
    return q.replace(PRICE_TAIL, ' ').replace(QUALIFIER_WORDS, ' ').replace(INCHES, ' ')
      .replace(/\s+/g, ' ').trim();
  }

  function stripTail(q) {
    // "couch for basement" / "dresser that fits ..." -> keep the head noun phrase
    return q.replace(/\b(for|that|which|to fit|to)\b .*$/, '').replace(/\s+/g, ' ').trim();
  }

  function stripStopwords(q) {
    var out = [];
    q.split(' ').forEach(function (t) {
      if (t && STOPWORDS.indexOf(' ' + t + ' ') === -1) out.push(t);
    });
    return out.join(' ');
  }

  function trySplit(token) {
    if (token.length < 6) return null;
    for (var i = 3; i <= token.length - 3; i++) {
      var a = token.slice(0, i), b = token.slice(i);
      if (WORDS[a] && WORDS[b]) return a + ' ' + b;
    }
    return null;
  }

  function notCarried(raw) {
    var q = norm(raw);
    if (!q) return null;
    for (var i = 0; i < NOT_CARRIED.length; i++) {
      var b = NOT_CARRIED[i];
      var padded = ' ' + q + ' ';
      if (padded.indexOf(' ' + b + ' ') === -1) continue;
      var rest = padded.replace(' ' + b + ' ', ' ').replace(/\s+/g, ' ').trim();
      var msg = (b === 'clearance' || b === 'liquidation')
        ? 'We don’t have a ' + b + ' section — but here are some options'
        : 'We don’t carry “' + b + '” — but here are some options';
      return { brand: b, rest: rest, message: msg };
    }
    return null;
  }

  // ordered retry candidates for the zero-result ladder
  function candidates(raw) {
    var q = norm(raw);
    if (!q) return [];
    var out = [];
    function push(c) { if (c && c !== q && out.indexOf(c) === -1) out.push(c); }

    push(applyPhrases(q) !== q ? applyPhrases(q) : null);
    push(applyDictionary(q));
    // carried-brand strip ("ashley couch" -> "couch") — engine may still miss
    // vendor+category combos even with vendor searched
    for (var i = 0; i < CARRIED_BRANDS.length; i++) {
      var b = CARRIED_BRANDS[i];
      if ((' ' + q + ' ').indexOf(' ' + b + ' ') !== -1) {
        push(replaceWord(q, b, '').replace(/\s+/g, ' ').trim());
        break;
      }
    }
    var noQual = stripQualifiers(applyDictionary(q));
    push(noQual);
    var noTail = stripTail(noQual || q);
    push(noTail);
    var noStop = stripStopwords(noTail || noQual || q).replace(SIZE_ADJ, ' ').replace(/\s+/g, ' ').trim();
    push(noStop);
    // joined-word split ("murphybed with mattress" tokens; generic rule)
    var base = (noStop || q).split(' ');
    if (base.length === 1) {
      push(trySplit(base[0]));
    } else if (base.length === 2) {
      push(base.join(''));  // "book shelf" -> "bookshelf"
    }
    // token drops FRONT-first (last token keeps Shopify's prefix matching)
    if (base.length >= 2) {
      for (var d = 1; d < base.length && d <= 3; d++) {
        push(base.slice(d).join(' '));
      }
    }
    return out;
  }

  return {
    norm: norm,
    preRewrite: preRewrite,
    notCarried: notCarried,
    candidates: candidates
  };
})();

/* FF FIX BATCH 2026-08-12 (item 8, /search page half) — EXTENDED by WAVE 3:
   the results page's zero-result rescue panel (snippets/ff-search-panel.liquid,
   server-rendered inside #product-grid — NOT inside a <predictive-search>) now
   runs the same rescue stack as the dropdown:
   (a) not-carried brands get the honest "We don't carry X" heading (no DYM, no
       ladder — the panel's categories/best sellers are the approved answer);
   (b) the "Did you mean X?" link (FIX BATCH item 8, unchanged);
   (c) the retry ladder probes rewrite candidates against suggest.json and
       redirects to the first non-empty one with ff_expanded=<original>, which
       makes main-search.liquid's existing inline script render the honest
       "Showing results for X · Search instead for Y" line (wave-1/2 pattern).
   ff_noexpand / ff_expanded / active facet filters all suppress the ladder so
   loops are impossible. */
(function () {
  function injectPageDym(q) {
    var panel = document.querySelector('#product-grid .ff-search-panel--noresults');
    if (!panel || !window.ffDidYouMean) return;
    if (panel.querySelector('[data-ff-didyoumean]')) return;
    var heading = panel.querySelector('.ff-search-noresults__heading');
    if (!heading) return;
    var s = window.ffDidYouMean(q);
    if (!s) return;
    var p = document.createElement('p');
    p.className = 'ff-search-didyoumean';
    p.setAttribute('data-ff-didyoumean', '');
    var a = document.createElement('a');
    a.className = 'ff-search-didyoumean__term';
    a.href = '/search?q=' + encodeURIComponent(s) + '&type=product&ff_noexpand=1';
    a.textContent = s;
    p.append('Did you mean ');
    p.appendChild(a);
    p.append('?');
    heading.insertAdjacentElement('afterend', p);
  }

  function run() {
    var panel = document.querySelector('#product-grid .ff-search-panel--noresults');
    if (!panel) return;
    var q = '', params;
    try {
      params = new URLSearchParams(window.location.search);
      q = (params.get('q') || '').trim();
    } catch (e) { return; }
    if (!q) return;

    var nc = window.ffSearch && window.ffSearch.notCarried(q);
    if (nc && !nc.rest) {
      var heading = panel.querySelector('.ff-search-noresults__heading');
      if (heading) heading.textContent = nc.message;
      return;
    }

    injectPageDym(q);

    var filtered = false;
    params.forEach(function (v, k) { if (k.indexOf('filter.') === 0) filtered = true; });
    if (filtered || params.get('ff_noexpand') || params.get('ff_expanded') || !window.ffSearch) return;

    var seen = {};
    /* hammer 3.3/3.4: did-you-mean auto-runs as the last ladder rung here too */
    var pageDym = window.ffDidYouMean ? window.ffDidYouMean(q) : null;
    var cands = (nc && nc.rest ? [nc.rest] : [])
      .concat(window.ffSearch.candidates(q))
      .concat(pageDym ? [pageDym] : [])
      .filter(function (c) { return c && c !== q && !seen[c] && (seen[c] = 1); })
      .slice(0, 7);
    var i = 0;
    function tryNext() {
      if (i >= cands.length) return;
      var cand = cands[i++];
      fetch('/search/suggest.json?q=' + encodeURIComponent(cand) +
            '&' + encodeURIComponent('resources[type]') + '=product' +
            '&' + encodeURIComponent('resources[limit]') + '=1' +
            '&' + encodeURIComponent('resources[options][fields]') + '=tag,title,product_type,variants.title,variants.sku,vendor',
            { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          var prods = data && data.resources && data.resources.results && data.resources.results.products;
          if (prods && prods.length) {
            var p2 = new URLSearchParams(window.location.search);
            p2.set('q', cand);
            p2.set('type', 'product');
            p2.set('ff_expanded', q);
            window.location.replace(window.location.pathname + '?' + p2.toString());
          } else {
            tryNext();
          }
        })
        .catch(function () { /* best-effort; DYM/no-results panel stays */ });
    }
    tryNext();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
